<template>
    <Drawer :title="title" initialWidth="2xl" @requestUnmount="hide">
        <template #default="{ hide }">
            <form @submit.prevent="save">
                <div class="pb-16 px-6">
                    <div class="bg-base pt-4">
						<p v-if="organisationsStore.errors?.generic" class="text-red-600 text-xs pb-4">
							{{ organisationsStore.errors.generic.toString() }}
						</p>

                        <div class="grid grid-cols-6 gap-10 row">

							<div class="col-span-6" v-if="!isGroup">
								<sms-input
									:autofocus="!inUpdateMode"
									:showValidationErrorMessage="true"
									type="text"
									v-model="editableModel.hubspot_id"
									:label="$t('Hubspot id')"
									:validationError="organisationsStore.errors?.fields.hubspot_id?.toLocaleString($i18n)"
									:disabled="editableModel.isNonCommercialAccount()"
									@change="prefillHubspotData()"
								></sms-input>

								<div class="">

									<span v-if="loadingHubspotData" class="text-xs italic text-gray-500">Loading hubspot data...</span>

									<span v-else-if="hubspotDataFound !== null">
										<span v-if="hubspotDataFound" class="text-green-600 text-xs">Valid hubspot company id.</span>
										<span v-if="!hubspotDataFound" class="text-red-600 text-xs">{{ hubspotErrorMessage }}</span>
									</span>

								</div>
							</div>

                            <div class="col-span-6">
                                <sms-input
									:autofocus="inUpdateMode || isGroup"
									:showValidationErrorMessage="true"
									type="text"
									v-model="editableModel.name"
									:label="$t('Name')"
									:validationError="organisationsStore.errors?.fields.name?.toLocaleString($i18n)"
								></sms-input>
                            </div>

                            <div v-if="!isGroup" class="col-span-6 sm:col-span-3">
                                <sms-select v-model="editableModel.subscription_plan"
                                    class="capitalize"
                                    :label="$t('Subscription plan')"
									:validationError="organisationsStore.errors?.fields.subscription_plan?.toLocaleString($i18n)"
                                    :options="subscriptionPlans.map((type: SubscriptionType) => {
                                        return {
                                            value: type.key,
                                            label: $t(type.label)
                                        }})"
                                >
                                </sms-select>
                            </div>

							<div v-if="!isGroup" class="col-span-6 sm:col-span-3">
								<sms-select v-model="editableModel.subscription_type"
											@change="handleChangedSubscriptionType"
											class="capitalize"
											:label="$t('Subscription type')"
											:validationError="organisationsStore.errors?.fields.subscription_type"
											:options="subscriptionTypes.map((type: SubscriptionType) => {
                                        return {
                                            value: type.key,
                                            label: $t(type.label)
                                        }})"
								>
								</sms-select>
							</div>

                            <div v-if="!isGroup" class="col-span-6 sm:col-span-3">
                                <sms-select v-model="editableModel.locale" :label="$t('Language')" class="capitalize"
                                                        :options="locales"></sms-select>
                            </div>
                            <div class="col-span-6 sm:col-span-3">
                                <sms-select v-model="editableModel.country" :label="$t('Country')" class="capitalize"
                                            :options="countries"></sms-select>
                            </div>
                            <div v-if="!isGroup" class="col-span-6 sm:col-span-3">
                                <sms-input type="date" v-model="terminationDateString" :label="$t('Termination date')"
										   :validationError="organisationsStore.errors?.fields.termination_date?.toLocaleString($i18n)"
                                                    :min="(new Date()).toISOString().split('T')[0]"
                                >
                                </sms-input>
                            </div>
                            <div v-if="!isGroup" class="col-span-6 sm:col-span-3">
                                <sms-select v-model="editableModel.curriculumId"
                                            class="capitalize"
                                            :label="$t('Curriculum')"
											:validationError="organisationsStore.errors?.fields.curriculum?.toLocaleString($i18n)"
                                            :options="curricula">
                                </sms-select>
                            </div>
							<div class="col-span-6 sm:col-span-3" v-if="!isGroup">
								<sms-select v-model="schoolGroupId"
											:label="$t('School group')"
											:validationError="organisationsStore.errors?.fields.school_group_id?.toLocaleString($i18n)"
											:options="groupOptions">
								</sms-select>
							</div>
                            <template v-if="!inUpdateMode && !isGroup">
                                <div class="col-span-6 font-bold text-lg border-b">
                                    {{ ('Account owner') }}
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <sms-input :showValidationErrorMessage="true" type="email" :label="$t('Email address')" :validationError="organisationsStore.errors?.fields.email?.toLocaleString($i18n)" v-model="ownerEmailAddress"></sms-input>
                                </div>
                                <div class="col-span-6">
                                    <input type="checkbox" id="send-invite" checked class="checkbox" v-model="sendInvitation" />
                                    <label for="send-invite" class="label-text pl-2 cursor-pointer">{{
                                            $t('Send an invitation email')
                                        }}</label>
                                </div>
                            </template>
                            <template v-if="inUpdateMode && !isGroup">
                                <div class="col-span-6 ">
                                    <p class="font-bold text-lg border-b">{{ ('Agenda settings') }}</p>
                                    <p class="alert alert-warning text-sm py-1 mt-6 items-start">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"></path></svg>
                                        {{ $t('Not required.') }}
                                        {{ $t('Only set school hours if deviating from regular school hours.') }}</p>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <sms-input type="time" v-model="editableModel.agenda_settings.school_hours_start" :label="$t('School hours start')"
                                    :validationError="organisationsStore.errors?.fields.agenda_settings?.school_hours_start?.toLocaleString($i18n)"
                                    step="60"
                                    >
                                    </sms-input>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <sms-input type="time" v-model="editableModel.agenda_settings.school_hours_end" :label="$t('School hours end')"
                                    :validationError="organisationsStore.errors?.fields.agenda_settings?.school_hours_end?.toLocaleString($i18n)"
                                    step="60"
                                    >
                                    </sms-input>
                                </div>
                                <div class="col-span-6">
                                    <input type="checkbox" id="include-weekends" checked class="checkbox" v-model="editableModel.agenda_settings.include_weekends" />
                                    <label for="include-weekends" class="label-text pl-2 cursor-pointer">{{$t('Include weekends in agenda') }}</label>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="drawer-action-bar" :class="[inUpdateMode ? 'justify-between' : 'justify-end']">
                    <Dropdown v-if="inUpdateMode" position="top-start">
                        <template #trigger>
                            <button type="button" class="btn" :class="[saving? 'btn-disabled':'btn-danger btn-outline']">
                                <span v-show="deleting" class="loading loading-spinner"></span>
                                <TrashIcon class="w-5 h-5"/>
                            </button>
                        </template>
                        <template #content="dialogActions">
                            <DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger"
                                                                            class="w-80" @cancel="dialogActions.close" @confirm="confirmRemove(dialogActions)">
                                <span class="text-danger">{{ $t('Are you sure?') }}</span>
                            </DropdownDialogConfirm>
                        </template>
                    </Dropdown>
                    <div class="flex"></div>

                    <div class="flex justify-end gap-1">
                        <button type="button" class="btn btn-ghost" @click="hide"
                            :class="[saving || deleting ? 'hidden' : '']">
                            {{ $t('Cancel') }}
                        </button>
                        <button type="submit" class="btn" :class="[deleting ? 'btn-disabled' : 'btn-primary']">
                            <span v-show="saving" class="loading loading-spinner"></span>
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </form>
        </template>
    </Drawer>
</template>

<script type="ts">
import {mapStores} from "pinia";
import { getCurriculumRegistry } from "@/config/curricula.registry";

import {Organisation} from '@/models/Organisation.model';
import {useOrganisationsStore} from "@/stores/Organisations.store";
import {ApiErrorMessageBag} from "@/stores/errors/ApiErrorMessageBag.ts";

import {SUPPORTED_LOCALES} from "@/i18n.ts";
import {SUPPORTED_COUNTRIES} from "@/i18n.ts";

import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import {TrashIcon} from "@heroicons/vue/24/outline";
import {SUBSCRIPTION_PLANS, SUBSCRIPTION_TYPES} from "@/config/organisations.config";
import Drawer from "@/components/ui/Drawer.v2.vue";
import {DateTime} from "luxon";
import config from "@/config/app.config.ts";

export default {
    emits: ['requestClose'],

    props: {
		organisation: {
			type: Organisation,
			required: false,
		},
		// Type of the new organisation -> determines the form fields and layout
		type: {
			type: String,
			default: 'school',
		},
	},
	components: {
		Drawer,
		Dropdown,
		DropdownDialogConfirm,
		TrashIcon,
	},
	data() {
		return {
			saving: false,
			deleting: false,
			originalModel: null,
			editableModel: null,
			ownerEmailAddress: null,
			sendInvitation: null,
			loadingHubspotData: false,
			hubspotDataFound: null,
			hubspotErrorMessage: null,
			groups: [],
		}
	},
	computed: {
		...mapStores(useOrganisationsStore),

		inUpdateMode() {
			return (this.organisation && this.organisation.id) ? true : false;
		},

		isGroup() {
			return this.editableModel?.type === 'group';
		},

		title() {
			if (this.isGroup) {
				return this.inUpdateMode ? this.$t('Edit school group') : this.$t('Add school group');
			}
			return this.inUpdateMode ? this.$t('Edit school') : this.$t('Add school');
		},

		/**
		 * Built from the registry so the id stored here cannot drift from the one goals carry
		 * and the goal-source file is named after — a mismatch silently empties the goal
		 * browser.
		 */
		curricula() {
			return [
				{ value: null, label: '' },
				...getCurriculumRegistry().map((entry) => ({ value: entry.id, label: entry.name })),
			];
		},

		locales() {
			let locales = [];
			for (const [key, value] of Object.entries(SUPPORTED_LOCALES)) {
				locales.push({
					value: key,
					label: value,
				});
			}
			return locales;
		},

		countries() {
			let countries = [];
			countries.push({
				value: null,
				label: '',
			})

			for (const [key, value] of Object.entries(SUPPORTED_COUNTRIES)) {
				countries.push({
					value: key,
					label: value,
				});
			}
			return countries;
		},

		subscriptionPlans() {
			return SUBSCRIPTION_PLANS;
		},

		subscriptionTypes() {
			return SUBSCRIPTION_TYPES;
		},


		terminationDateString: {
			get() {
				if (!this.editableModel.termination_date) {
					return '';
				}
				return this.editableModel.termination_date.toFormat('yyyy-MM-dd');
			},

			set(dateString) {
				if (!dateString) {
					this.editableModel.termination_date = null;
					return;
				}
				this.editableModel.termination_date = DateTime.fromFormat(dateString, 'yyyy-MM-dd');
			}
		},

		groupOptions() {
			const options = [{ value: null, label: '' }];
			this.groups.forEach((group) => {
				if (this.editableModel && group.id === this.editableModel.id) {
					return; // an org cannot be its own group
				}
				options.push({ value: group.id, label: group.name });
			});
			return options;
		},

		schoolGroupId: {
			get() {
				return this.editableModel?.schoolGroup?.id ?? null;
			},
			set(value) {
				if (!value) {
					this.editableModel.schoolGroup = null;
					return;
				}
				const group = this.groups.find(g => g.id === value);
				this.editableModel.schoolGroup = group ? { id: group.id, name: group.name } : { id: value, name: '' };
			}
		},
	},

	methods: {
		hide() {
			this.organisationsStore.clearErrors();
			this.$emit('requestClose');
		},

		setDefaultTrialTerminationDate() {
			this.editableModel.termination_date = DateTime.now().plus({ days: 30 });
		},

		handleChangedSubscriptionType(event) {
			if (event.target.value !== 'trial') {
				this.terminationDateString = null;
			} else {
				this.setDefaultTrialTerminationDate();
			}
		},

		async confirmRemove(dialogActions) {

			if (this.deleting) {
				return;
			}
			this.deleting = true;

			try {
				await this.organisationsStore.delete(this.originalModel);
				this.deleting = false;

				this.hide();

				this.$notify({
					text: this.isGroup ? this.$t('The school group has been removed') : this.$t('The school has been removed'),
					type: "success",
				});

			} catch (e) {
				// error message is populated now.
				this.deleting = false;
				dialogActions?.close();
				if (!(e instanceof ApiErrorMessageBag)) {
					this.$notify({
						title: "Error!",
						text: e.toString(),
						type: "error"
					});
					throw e;
				}
			}
		},

		async save(event) {

			if (this.saving) {
				return;
			}
			this.saving = true;

			try {

				// Should we include email address?
				if (this.editableModel.hasId()) {
					await this.organisationsStore.save(this.editableModel);
				} else {
					await this.organisationsStore.create(this.editableModel, this.ownerEmailAddress, this.sendInvitation || false);
				}

				this.saving = false;
				this.hide();

				this.$notify({
					text: this.isGroup ? this.$t('The school group has been saved') : this.$t('The school has been saved'),
					type: "success",
				});

			} catch (e) {
				// error message is populated now.
				this.saving = false;
				if (!(e instanceof ApiErrorMessageBag)) {
					this.$notify({
						title: "Error!",
						text: e.toString(),
						type: "error"
					});
					throw e;
				}
			}
		},

		async prefillHubspotData() {
			this.loadingHubspotData = true;

			const result = await this.organisationsStore.prefillHubspotData(this.editableModel);;
			if (result === true) {
				this.hubspotDataFound = true;
			} else if (result === null) {
				this.hubspotDataFound = null;
			} else {
				this.hubspotDataFound = false;
				this.hubspotErrorMessage = result;
			}

			this.loadingHubspotData = false;
		},
	},

	beforeMount() {
		if (this.organisation && this.organisation.id) {
			this.originalModel = this.organisation;
			// clone the store model to allow for manipulation without instantly affecting the store
			this.editableModel = this.organisation.clone();

		} else {
			this.editableModel = this.organisationsStore.new();
			if (this.type === 'group') {
				this.editableModel.type = 'group';
				// Groups have no subscription of their own.
				this.editableModel.subscription_type = 'internal';
			} else {
				this.setDefaultTrialTerminationDate();
			}
		}

		this.organisationsStore.loadGroupOrganisations().then((groups) => {
			this.groups = groups;
		});
	},
}
</script>
